import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { useNodes, useStacks } from "../../api/queries";
import type { StackMeta } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { StackStatusPill } from "../../components/StatePill";
import { useFleet } from "../../fleet/FleetContext";
import { DeployStackModal } from "./DeployStackModal";

function buildColumns(nodeLabel: (nodeId: string) => string): DataTableColumn<StackMeta>[] {
  return [
    {
      key: "name",
      header: "Name",
      cell: (stack) => (
        <>
          {/* stopPropagation: the row itself is also a click target (DataTable's
              onRowClick); without this, activating the link would navigate twice. */}
          <Link
            className="data-table__primary-link"
            href={`/stacks/${stack.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {stack.name}
          </Link>
        </>
      ),
    },
    { key: "status", header: "Status", cell: (stack) => <StackStatusPill status={stack.status} /> },
    { key: "node", header: "Node", cell: (stack) => nodeLabel(stack.nodeId) },
    {
      key: "updated",
      header: "Updated",
      cell: (stack) => new Date(stack.updatedAt).toLocaleString(),
      className: "data-table__secondary",
    },
  ];
}

export function StacksListPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch, isFetching } = useStacks(currentId, {
    cursor: paginator.cursor,
  });
  const { data: nodesPage, isPending: nodesPending } = useNodes(currentId);
  const [deployOpen, setDeployOpen] = useState(false);

  // Deny-by-default mirrors the server's authz matrix (server/internal/authz):
  // viewer has stack:view only, operator+ also has create/update/delete. This
  // only hides the control — the server enforces the real decision on every
  // request regardless of what this check says.
  const canDeploy = session?.role !== "viewer";

  // A stack only carries its node's id on the wire (see api/types.ts) — the
  // name is resolved by joining against the nodes list, same as any other
  // client would have to. While that join is still loading, show a dash
  // rather than flashing the raw id first.
  const nodeNameById = new Map((nodesPage?.items ?? []).map((node) => [node.id, node.name]));
  function nodeLabel(nodeId: string): string {
    if (nodesPending) return "—";
    return nodeNameById.get(nodeId) ?? nodeId;
  }

  const columns = buildColumns(nodeLabel);

  const deployButton = (
    <Button variant="primary" onClick={() => setDeployOpen(true)}>
      Deploy stack
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Stacks"
        description="Compose stacks stay file-native — Comebye deploys and tracks them, it never becomes the only copy."
        actions={canDeploy ? deployButton : undefined}
      />

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading stacks…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={error instanceof Error ? error.message : "Couldn't load stacks. Check your connection and try again."}
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No stacks deployed yet"
            description="Deploy a Compose file to a node in this fleet to see it tracked here."
            action={canDeploy ? deployButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={columns}
              rows={data.items}
              rowKey={(stack) => stack.id}
              onRowClick={(stack) => navigate(`/stacks/${stack.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="stacks"
            />
          </>
        )}
      </QueryStates>

      {canDeploy && <DeployStackModal open={deployOpen} fleetId={currentId} onClose={() => setDeployOpen(false)} />}
    </>
  );
}
